/**
 * HTTP client with retry logic and rate limiting
 */

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
  retry?: boolean;
  maxRetries?: number;
  retryDelay?: number;
}

export class HttpError extends Error {
  constructor(public status: number, public statusText: string) {
    super(`HTTP ${status}: ${statusText}`);
    this.name = 'HttpError';
  }
}

export class HttpClient {
  private static instance: HttpClient;
  private requestQueue: Map<string, number> = new Map();
  // Reduce throttling in development mode
  private readonly MIN_REQUEST_INTERVAL = import.meta.env.DEV ? 10 : 100; // 10ms in dev, 100ms in prod
  private appStartTime = Date.now();
  private readonly APP_STARTUP_PERIOD = 10000; // 10 seconds - be more conservative during startup
  
  private constructor() {}
  
  static getInstance(): HttpClient {
    if (!HttpClient.instance) {
      HttpClient.instance = new HttpClient();
    }
    return HttpClient.instance;
  }
  
  async request<T>(url: string, options: RequestOptions = {}): Promise<T> {
    const {
      method = 'GET',
      headers = {},
      body,
      timeout = 30000,
      retry = true,
      maxRetries = import.meta.env.DEV ? 5 : 3,  // More retries in dev
      retryDelay = this.getRetryDelay()  // Dynamic retry delay based on app state
    } = options;
    
    // Rate limiting per endpoint
    await this.throttleRequest(url);
    
    const makeRequest = async (attempt: number = 0): Promise<T> => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      try {
        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...headers
          },
          body: body ? JSON.stringify(body) : undefined,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          // Handle rate limiting specifically
          if (response.status === 429) {
            const retryAfter = response.headers.get('Retry-After');
            // During app startup, use longer delays
            const isStartup = Date.now() - this.appStartTime < this.APP_STARTUP_PERIOD;
            const baseDelay = isStartup ? retryDelay * 2 : retryDelay;
            const delay = retryAfter ? parseInt(retryAfter) * 1000 : Math.min(baseDelay * Math.pow(2, attempt), 30000);
            
            if (retry && attempt < maxRetries) {
              console.warn(`[HttpClient] Rate limited, retrying after ${delay}ms...`);
              await new Promise(resolve => setTimeout(resolve, delay));
              return makeRequest(attempt + 1);
            }
          }
          
          const errorText = await response.text();
          throw new HttpError(response.status, errorText || response.statusText);
        }
        
        const text = await response.text();
        return text ? JSON.parse(text) : {} as T;
        
      } catch (error) {
        clearTimeout(timeoutId);
        
        if (error instanceof HttpError) {
          // Don't retry client errors (4xx) except 429 and 408
          if (error.status >= 400 && error.status < 500 && error.status !== 429 && error.status !== 408) {
            throw error;
          }
          
          // For 429, use exponential backoff
          if (error.status === 429 && retry && attempt < maxRetries) {
            // During app startup, use longer delays
            const isStartup = Date.now() - this.appStartTime < this.APP_STARTUP_PERIOD;
            const baseDelay = isStartup ? retryDelay * 2 : retryDelay;
            const delay = Math.min(baseDelay * Math.pow(2, attempt), 30000);
            console.warn(`[HttpClient] Rate limited (429), retrying after ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            return makeRequest(attempt + 1);
          }
        }
        
        // Retry on network errors
        if (retry && attempt < maxRetries) {
          const delay = Math.min(retryDelay * Math.pow(2, attempt), 10000);
          console.warn(`[HttpClient] Request failed, retrying in ${delay}ms...`, error);
          await new Promise(resolve => setTimeout(resolve, delay));
          return makeRequest(attempt + 1);
        }
        
        // Check if error is QUIC-related and add special handling
        if (error instanceof Error && error.message.includes('ERR_QUIC_PROTOCOL_ERROR')) {
          console.warn('[HttpClient] QUIC protocol error detected. Consider using HTTP/1.1 fallback');
        }
        
        throw error;
      }
    };
    
    return makeRequest();
  }
  
  /**
   * Get dynamic retry delay based on app state
   */
  private getRetryDelay(): number {
    const timeSinceStart = Date.now() - this.appStartTime;
    const isStartup = timeSinceStart < this.APP_STARTUP_PERIOD;
    
    // Use longer delays during startup to prevent rate limiting
    if (isStartup) {
      return import.meta.env.DEV ? 1000 : 2000; // 1s in dev, 2s in prod during startup
    }
    
    return import.meta.env.DEV ? 500 : 1000; // Normal delays after startup
  }
  
  /**
   * Throttle requests to prevent rate limiting
   */
  private async throttleRequest(url: string): Promise<void> {
    let endpoint: string;
    
    try {
      // Try to parse as absolute URL
      const urlObj = new URL(url);
      endpoint = urlObj.pathname;
    } catch {
      // If not absolute URL, treat as relative path
      endpoint = url.startsWith('/') ? url : '/' + url;
    }
    
    const lastRequest = this.requestQueue.get(endpoint);
    const now = Date.now();
    
    // Add extra delay during app startup (first 10 seconds)
    const timeSinceStart = now - this.appStartTime;
    const startupDelay = timeSinceStart < this.APP_STARTUP_PERIOD ? 500 : 0; // 500ms extra delay during startup
    const minInterval = this.MIN_REQUEST_INTERVAL + startupDelay;
    
    if (lastRequest) {
      const elapsed = now - lastRequest;
      if (elapsed < minInterval) {
        await new Promise(resolve => setTimeout(resolve, minInterval - elapsed));
      }
    }
    
    this.requestQueue.set(endpoint, Date.now());
    
    // Clean up old entries
    if (this.requestQueue.size > 100) {
      const cutoff = Date.now() - 60000; // 1 minute ago
      for (const [key, time] of this.requestQueue.entries()) {
        if (time < cutoff) {
          this.requestQueue.delete(key);
        }
      }
    }
  }
}